import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Plus, X, FlipHorizontal2 } from 'lucide-react';
import type { CardCatalog } from '../types';
import { TopicCard } from './TopicCard';
import { CardPicker } from './CardPicker';
import { useT } from '../i18n';

interface Props {
  catalog: CardCatalog;
  picked: number[];                       // ordered topic ids
  onChange: (ids: number[]) => void;
  /** Times each topic was chosen across all sessions INCLUDING today. */
  recurrence: Map<number, number>;
  /** Header caption, e.g. "La table d'aujourd'hui — mercredi 16 juillet". */
  caption: string;
  disabled?: boolean;
  /** Rendered under the spread, inside the mat (notes + observations). */
  children?: ReactNode;
  /** Rendered after everything, full mat width (e.g. the copy-report action). */
  footer?: ReactNode;
  /** Flip the mat to its history face. */
  onFlip?: () => void;
  flipLabel?: string;
}

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

const MAX_SUGGESTIONS = 7;

/**
 * "La table d'aujourd'hui" — the participant's picked cards laid out large,
 * like cards on the table in the room. Adding a card is an empty slot that
 * becomes a type-ahead; the full deck only opens on explicit request.
 */
export function CardTable({ catalog, picked, onChange, recurrence, caption, disabled, children, footer, onFlip, flipLabel }: Props) {
  const { t } = useT();
  const [slotOpen, setSlotOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [deckOpen, setDeckOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);

  const cardById = useMemo(() => new Map(catalog.cards.map((c) => [c.id, c])), [catalog]);
  const catById = useMemo(() => new Map(catalog.categories.map((c) => [c.id, c])), [catalog]);
  const pickedSet = useMemo(() => new Set(picked), [picked]);

  const suggestions = useMemo(() => {
    if (!slotOpen) return [];
    const q = normalize(query.trim());
    return catalog.cards
      .filter((c) => c.active !== false && !pickedSet.has(c.id))
      .filter((c) => !q || normalize(c.name).includes(q))
      .sort((a, b) => {
        if (q) {
          const ap = normalize(a.name).startsWith(q) ? 0 : 1;
          const bp = normalize(b.name).startsWith(q) ? 0 : 1;
          if (ap !== bp) return ap - bp;
        }
        return (a.pdf_card_number ?? 999) - (b.pdf_card_number ?? 999);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [slotOpen, query, catalog.cards, pickedSet]);

  useEffect(() => {
    if (slotOpen) inputRef.current?.focus();
  }, [slotOpen]);

  // Close the open slot when clicking anywhere outside it.
  useEffect(() => {
    if (!slotOpen) return;
    const onDown = (e: PointerEvent) => {
      if (slotRef.current && !slotRef.current.contains(e.target as Node)) {
        setSlotOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [slotOpen]);

  const add = (id: number) => {
    if (!pickedSet.has(id)) onChange([...picked, id]);
    setQuery('');
    inputRef.current?.focus();
  };
  const remove = (id: number) => onChange(picked.filter((p) => p !== id));

  return (
    <div className="mat">
      <div className="mat-head">
        <span className="mat-caption">{caption}</span>
        <span className="mat-head-actions">
          {/* The "Tout le jeu…" header button was removed (PV ae45 §5.7: nobody
              used it); the deck still opens from the add-card slot. */}
          {onFlip && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={onFlip}>
              <FlipHorizontal2 size={14} /> {flipLabel}
            </button>
          )}
        </span>
      </div>

      <div className="mat-spread">
        {picked.map((id) => {
          const c = cardById.get(id);
          if (!c) return null;
          const cat = c.category_id != null ? catById.get(c.category_id) : null;
          const count = recurrence.get(id) ?? 1;
          return (
            <TopicCard
              key={id}
              name={c.name}
              number={c.pdf_card_number}
              color={cat?.color}
              size="md"
              className="mat-card"
              recurrence={count}
              recurrenceTitle={t('cards.recurrence').replace('{n}', String(count))}
              onRemove={disabled ? undefined : () => remove(id)}
            />
          );
        })}

        {picked.length === 0 && disabled && (
          <span className="mat-empty">{t('cards.noPicks')}</span>
        )}

        {!disabled && !slotOpen && (
          <button type="button" className="mat-slot" onClick={() => setSlotOpen(true)}>
            <Plus size={20} />
            <span>{t('cards.addCard')}</span>
          </button>
        )}

        {!disabled && slotOpen && (
          <div className="mat-slot mat-slot--open" ref={slotRef}>
            <input
              ref={inputRef}
              type="text"
              className="mat-slot-input"
              placeholder={t('cards.searchPlaceholder')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && suggestions[0]) add(suggestions[0].id);
                if (e.key === 'Escape') { setSlotOpen(false); setQuery(''); }
              }}
              aria-label={t('cards.searchPlaceholder')}
            />
            <div className="mat-slot-list">
              {suggestions.map((c) => {
                const cat = c.category_id != null ? catById.get(c.category_id) : null;
                return (
                  <button key={c.id} type="button" className="mat-slot-opt" onClick={() => add(c.id)}>
                    <span className="card-cat-chip-dot" style={{ background: cat?.color ?? 'var(--line-strong)' }} />
                    <span className="mat-slot-opt-name">{c.name}</span>
                    {c.pdf_card_number != null && <span className="mat-slot-opt-num">{c.pdf_card_number}</span>}
                  </button>
                );
              })}
              {suggestions.length === 0 && (
                <span className="mat-slot-none">{t('cards.noMatches')}</span>
              )}
              <button
                type="button"
                className="mat-slot-all"
                onClick={() => { setDeckOpen(true); setSlotOpen(false); setQuery(''); }}
              >
                {t('cards.browseDeck')}
              </button>
            </div>
          </div>
        )}
      </div>

      {children && <div className="mat-under">{children}</div>}
      {footer && <div className="mat-footer">{footer}</div>}

      {deckOpen && (
        <div className="modal-backdrop" onClick={() => setDeckOpen(false)}>
          <div className="modal-card deck-modal" onClick={(e) => e.stopPropagation()}>
            <div className="deck-modal-head">
              <div className="modal-title" style={{ margin: 0 }}>{t('cards.deckTitle')}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setDeckOpen(false)} aria-label={t('common.close')}>
                <X size={16} />
              </button>
            </div>
            <CardPicker catalog={catalog} picked={picked} onChange={onChange} />
          </div>
        </div>
      )}
    </div>
  );
}
